import {Code} from 'src/design/atoms/Code';
import {Group} from 'src/design/atoms/Group';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import type {MRT_ColumnDef} from 'mantine-react-table';
import {useMemo} from 'react';
import type {CloudWatchLogEventResponse} from 'src/api/ProactApiClient';
import {Pill} from 'src/design/atoms/Pill';
import {ViewField} from 'src/design/atoms/ViewField';
import {useTable, useTableState} from 'src/design/organisms/useTable';
import {LOG_LEVEL_META, formatLogTimestamp} from '../cloudWatchDisplay';

// ViewField renders its value inside a <p>, so Code stays inline (`<code>`) and takes
// the block look from CSS; `block` would emit a <pre> and break DOM nesting.
const CODE_BLOCK_STYLE = {
  display: 'block',
  whiteSpace: 'pre-wrap',
  fontSize: 11,
  padding: 'var(--mantine-spacing-xs)',
} as const;

export interface CloudWatchLogsTableProps {
  events: CloudWatchLogEventResponse[];
  isFetching: boolean;
  isError: boolean;
  error?: string;
  hasSearched: boolean;
}

export const CloudWatchLogsTable = ({
  events,
  isFetching,
  isError,
  error,
  hasSearched,
}: CloudWatchLogsTableProps) => {
  const columns = useMemo<MRT_ColumnDef<CloudWatchLogEventResponse>[]>(
    () => [
      {
        id: 'level',
        header: 'Level',
        size: 130,
        accessorFn: row => LOG_LEVEL_META[row.level].label,
        Cell: ({row}) => {
          const meta = LOG_LEVEL_META[row.original.level];
          return <Pill text={meta.label} color={meta.color} />;
        },
      },
      {
        id: 'timestamp',
        header: 'Time',
        size: 170,
        accessorFn: row => formatLogTimestamp(row.timestamp),
      },
      {
        accessorKey: 'message',
        header: 'Message',
        size: 620,
        Cell: ({row}) => (
          <Text size="xs" ff="monospace" lineClamp={1}>
            {row.original.message}
          </Text>
        ),
      },
      {
        accessorKey: 'logStream',
        header: 'Stream',
        size: 180,
        Cell: ({row}) => (
          <Text size="xs" c="dimmed" lineClamp={1}>
            {row.original.logStream ?? '—'}
          </Text>
        ),
      },
    ],
    []
  );

  const tableState = useTableState({
    identifier: 'cloudwatch-logs',
    columns,
    defaultDensity: 'xs',
    defaultPagination: {pageIndex: 0, pageSize: 50},
  });

  const {table} = useTable({
    ...tableState,
    hideResetAction: true,
    tableOptions: {
      manualPagination: false,
      manualSorting: false,
      manualFiltering: false,
      enableSorting: false,
      enableColumnFilters: false,
      enableRowActions: false,
      columns,
      data: events,
      getRowId: (row, index) => row.id ?? `event-${index}`,
      renderDetailPanel: ({row}) => (
        <Stack gap="sm" px="xl" py="md">
          <ViewField
            label="Message"
            value={
              <Text span size="sm" style={{whiteSpace: 'pre-wrap'}}>
                {row.original.message}
              </Text>
            }
          />
          {row.original.exception && (
            <ViewField
              label="Exception"
              value={
                <Code style={CODE_BLOCK_STYLE}>{row.original.exception}</Code>
              }
            />
          )}
          <Group gap="xl" align="flex-start">
            <ViewField
              label="Correlation id"
              value={row.original.correlationId ?? '—'}
            />
            <ViewField label="User id" value={row.original.userId ?? '—'} />
            <ViewField
              label="Instance id"
              value={row.original.instanceId ?? '—'}
            />
            <ViewField label="Stream" value={row.original.logStream ?? '—'} />
          </Group>
          <ViewField
            label="Raw line"
            value={<Code style={CODE_BLOCK_STYLE}>{row.original.raw}</Code>}
          />
        </Stack>
      ),
      renderEmptyRowsFallback: () => (
        <Text c="dimmed" ta="center" py="xl" size="sm">
          {hasSearched
            ? 'No log events matched this filter in the selected window. Widen the range or clear the filter text.'
            : 'Choose a log group and range, then run Search to load events.'}
        </Text>
      ),
    },
    totalCount: events.length,
    isLoading: false,
    isFetching,
    isError,
    error,
  });

  return table;
};
